import React, { useEffect, useRef, useState } from "react";

/**
 * 360° diamond viewer frame.
 *
 * The v360.diamonds embed lays out the diamond + playback chrome relative
 * to ITS OWN viewport size. When we constrain the iframe to a small
 * mobile card (~160 px), the viewer pushes the diamond off-centre toward
 * the top-right of its internal canvas.
 *
 * To keep the diamond perfectly centred at every card size, we render
 * the iframe at its NATIVE 600×600 px size (where the viewer naturally
 * centres the stone) and use a CSS transform: scale() to shrink it down
 * to fit the card. The container is a flex centred 1:1 box, so the
 * scaled iframe is then horizontally + vertically centred at every
 * breakpoint with no clipping or offset.
 */
const NATIVE_PX = 600;

export default function Solitaire360Frame({
  src,
  title,
  interactive = false,
  testId,
  className = "",
}) {
  const boxRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!boxRef.current) return;
    const update = () => {
      const w = boxRef.current?.clientWidth || 0;
      if (w > 0) setScale(w / NATIVE_PX);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={boxRef}
      className={`relative w-full overflow-hidden ${className}`}
      style={{
        aspectRatio: "1 / 1",
        background: "#0B1A33",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {src ? (
        <iframe
          src={src}
          title={title}
          loading="lazy"
          referrerPolicy="no-referrer"
          allow="autoplay; fullscreen"
          sandbox="allow-scripts allow-same-origin allow-popups"
          data-testid={testId}
          style={{
            width: `${NATIVE_PX}px`,
            height: `${NATIVE_PX}px`,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            pointerEvents: interactive ? "auto" : "none",
            background: "#fafafa",
            flex: "0 0 auto",
            display: "block",
          }}
        />
      ) : null}
    </div>
  );
}
